import { ROOT_URL } from "fleet/constant/media-library";
import { PRIORITY_MAIN, PRIORITY_FALLBACK } from "fleet/constant/strategy-v2";
import { PropTypeDenormalizedStrategy } from "fleet/prop-types/strategy";
import capitalize from "lodash/capitalize";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import React from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import { FormLabel as BsFormLabel } from "react-bootstrap";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import Row from "react-bootstrap/Row";
import { Redirect } from "react-router-dom";
import { isAdmin, isOperator } from "redux-store/users/selectors";
import { BackButton } from "ui/button/back";
import { FormLabel } from "ui/form-label";
import { NavLoader } from "ui/loader";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";
import { isValidViewType, VIEW } from "view/views";

import { SpotList } from "./spot-list";
import { normalizeStrategy } from "./utils";

class StrategyV2Detail extends React.PureComponent {
  static get propTypes() {
    return {
      applications: PropTypes.object,
      canSubmit: PropTypes.bool.isRequired,
      currentStrategy: PropTypes.object.isRequired,
      editStrategy: PropTypes.func.isRequired,
      id: PropTypes.string,
      newStrategy: PropTypes.func.isRequired,
      redirectTo: PropTypes.number,
      referenceDnStrategy: PropTypes.object.isRequired,
      resetUiRedirect: PropTypes.func.isRequired,
      setUiStrategy: PropTypes.func.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      strategy: PropTypeDenormalizedStrategy.isRequired,
      user: PropTypes.object.isRequired,
      viewType: PropTypes.oneOf([VIEW.CLONE, VIEW.CREATE, VIEW.EDIT])
        .isRequired,
    };
  }

  static get defaultProps() {
    return {
      applications: {},
    };
  }

  constructor() {
    super();

    this.canEdit = this.canEdit.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.saveStrategy = this.saveStrategy.bind(this);
    this.strategyHasChanged = this.strategyHasChanged.bind(this);
  }

  componentWillUnmount() {
    this.props.resetUiRedirect();
  }

  handleNameChange(e) {
    const { strategy, setUiStrategy } = this.props;
    const name = e.target.value;

    if (name === strategy.name) {
      return;
    }

    setUiStrategy({
      ...strategy,
      name,
    });
  }

  saveStrategy() {
    const {
      applications,
      editStrategy,
      id,
      newStrategy,
      viewType,
      strategy: denormalized,
    } = this.props;
    const strategy = normalizeStrategy(applications, denormalized);

    if ([VIEW.CLONE, VIEW.CREATE].includes(viewType)) {
      newStrategy(strategy);
    } else {
      editStrategy(id, strategy);
    }
  }

  canEdit() {
    const { viewType, user } = this.props;

    if (isAdmin(user)) {
      return true;
    }

    // Strategy model does not have the concept of user ownership. Therefore
    // an operator is only allowed to clone a strategy + edit the clone
    // strategy name. After a strategy is created, operators won't be able to
    // edit any part of a strategy.
    return viewType === VIEW.CLONE && isOperator(user);
  }

  _renderRedirect(strategyId) {
    const pathname = `${ROOT_URL}/${strategyId}/${VIEW.EDIT}`;
    const to = {
      pathname: "/redirect",
      search: `to=${pathname}`,
    };

    return <Redirect to={to} />;
  }

  strategyHasChanged() {
    const { referenceDnStrategy, currentStrategy } = this.props;

    return !isEqual(referenceDnStrategy, currentStrategy);
  }

  renderView() {
    const { canSubmit, viewType, strategy, storesLoaded, user } = this.props;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    const { name, main, fallback } = strategy;
    const saveButtonTitle =
      viewType === VIEW.EDIT ? "Update Strategy" : "Create Strategy";

    return (
      <div>
        {this.strategyHasChanged() && (
          <Row>
            <Col xs={12}>
              <Alert variant="info">
                <i className="fa fa-info-circle" aria-hidden="true" />
                You may have unsaved changes.
              </Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col sm={12} md={6}>
            <FormGroup controlId="name">
              <FormLabel as={BsFormLabel} required>
                Strategy Name
              </FormLabel>
              <FormControl
                type="text"
                required={true}
                placeholder="Strategy Name"
                name="name"
                key="name"
                value={name}
                onChange={this.handleNameChange}
                disabled={!this.canEdit()}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <SpotList
              priority={PRIORITY_MAIN.id}
              buttonClass="primary"
              disabled={!isAdmin(user)}
              spots={main}
              strategy={strategy}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <SpotList
              priority={PRIORITY_FALLBACK.id}
              buttonClass="default"
              disabled={!isAdmin(user)}
              spots={fallback}
              strategy={strategy}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <div className="controls pull-right">
              <BackButton />
              <Button
                variant="primary"
                name="submit"
                disabled={!canSubmit}
                onClick={this.saveStrategy}
              >
                {saveButtonTitle}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  render() {
    const { viewType, redirectTo } = this.props;

    if (!isValidViewType(viewType)) {
      return <Redirect to="/404" />;
    }

    if (redirectTo) {
      return this._renderRedirect(redirectTo);
    }

    const pageTitle = `Strategy - ${capitalize(viewType)} Strategy`;
    const view = this.renderView();

    return (
      <Page title={pageTitle} className="strategy-v2">
        <Box title="" noHeader={true}>
          {view}
        </Box>
      </Page>
    );
  }
}

export { StrategyV2Detail };
